import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import {
  ConsentCheckboxOptionsData,
  ConsentCheckboxOptionsSignatures,
  Signatures
} from '@app/services/selective-processes/component/lgpd/lgpd-consent-checkbox-options';
import { Fields } from '@app/services/selective-processes/fields/fields';
import { validValue } from '@shared/utils/util';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';

@Component({
  selector: 'app-lgpd-signatures',
  templateUrl: './lgpd-signatures.component.html',
  styleUrls: ['./lgpd-signatures.component.scss']
})
export class LgpdSignaturesComponent implements OnChanges {

  @Input() selectedConsentSignatures: Array<ConsentCheckboxOptionsSignatures>;
  @Input() consentCheckboxOptionsData: Array<ConsentCheckboxOptionsData>;
  @Input() availableSignatures: Array<Signatures>;
  @Input() nonMultivalueFields: Array<Fields>;
  @Input() controlName: string;
  @Input() form: FormGroup;
  @Input() consentId: string;

  public formControlSignatures: FormArray;
  public editorInFocus = false;
  public reset = [[]];

  public selectable = true;
  public removable = true;

  public filteredSignatures: Observable<any>;
  public autoCompleteSignatureCtrl = new FormControl();
  @ViewChild('signatureInput') signatureInput: ElementRef<HTMLInputElement>;
  @ViewChild('autocompleteSignatures') matAutocomplete: MatAutocomplete;
  
  get signatureForm() {return this.form};

  constructor(private formBuilder: FormBuilder) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes.consentId.firstChange) {
      this.formControlSignatures.clear();
    }
    const currentSignatures = changes.selectedConsentSignatures.currentValue.signatures;
    this.buildSignature(currentSignatures);
    this.initAutoCompleteSignature();
  }

  private addSignature(signature?: ConsentCheckboxOptionsSignatures): void {
    this.formControlSignatures = this.form.get('signatures') as FormArray;
    this.formControlSignatures.push(this.formBuilderSignature(signature));
    this.saveFieldByLegitimateInterests();
    this.updatePositionsOfTheSignaturesOnTheForm();
  }

  private buildSignature(signatures: Array<ConsentCheckboxOptionsSignatures>): void {
    signatures.map(signature => this.addSignature(signature));
  }

  private findSignatureByTypeId(signature): Signatures {
    if (typeof signature === 'object') {
      return signature;
    }
    return this.availableSignatures.filter(subs => subs.id === String(signature))[0];
  }

  private updatePositionsOfTheSignaturesOnTheForm(): void {
    this.formControlSignatures.controls.map((control, index) => {
      control.patchValue({position: index + 1}, {emitEvent: true});
    });
  }

  public disablesAlreadySelectedSignatures(availableSignature: Signatures): boolean {
    const findSignature: Array<Signatures> = this.formControlSignatures.value.filter(signature => {
      if (signature.signature_id) {
        return signature.signature_id.id === availableSignature.id;
      }
    });
    if (findSignature.length > 0) {
      return true;
    }
    return false;
  }

  public deleteSignature(index: number): void {
    this.formControlSignatures.removeAt(index);
    this.updatePositionsOfTheSignaturesOnTheForm();
  }

  public changesPositionOfTheSignatures(event: CdkDragDrop<string[]>): void {
    this.reset[0] = [];
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.formControlSignatures.controls, event.previousIndex, event.currentIndex);
      this.updatePositionsOfTheSignaturesOnTheForm();
    }
  }

  private saveFieldByLegitimateInterests() {
    this.form.valueChanges.subscribe(changed => {
      if (changed.field_id_to_save_legitimate_interests && changed.consent_id === '3') {
        this.setFieldIdToSaveSignature(changed.field_id_to_save_legitimate_interests.field_id);
      }
    });
  }

  private setFieldIdToSaveSignature(fieldId: number): void {
    let emitEventAfterSetValue = false;
    this.formControlSignatures.controls.map(control => {
      if (validValue(fieldId) && control.get('field_id_to_save_signature').value !== fieldId) {
        control.patchValue({field_id_to_save_signature: fieldId}, {emitEvent: false});
        emitEventAfterSetValue = true;
      }
    });
    if (emitEventAfterSetValue) {
      this.formControlSignatures.updateValueAndValidity();
    }
  }

  private formBuilderSignature(signature) {
    const signatureEnglish = this.convertSignatureDataToEnglish(signature);
    const signatureFormGroup = this.formBuilder.group({
      id: signatureEnglish.id ?? null,
      field_id_to_save_signature: [signatureEnglish.field_id_to_save_signature ?? null, Validators.required],
      signature_id: [this.findSignatureByTypeId(signatureEnglish.signature_id) ?? null, Validators.required],
      label: [signatureEnglish.label ?? null, Validators.required],
      required: signatureEnglish.required ?? false,
      position: signatureEnglish.position ?? null
    });
    this.setSignatureIDValueChanges(signatureFormGroup);
    return signatureFormGroup;
  }

  setSignatureIDValueChanges(group) {
    group.get('signature_id').valueChanges.subscribe((signature_id)=> {
      const signature = this.findSignatureByTypeId(signature_id);
      const label = this.getNameLabel(signature);
      group.get('label').setValue(label, {emitEvent: false});
    });
  }

  private convertSignatureDataToEnglish(signature) {
    if (signature.id || signature.signature_id) {
      return signature;
    }

    return {
      id: null,
      field_id_to_save_signature: signature.field_id_to_save_signature ?? null,
      signature_id: signature.idTipo ?? signature.signature_id,
      label: this.getNameLabel(signature),
      required: signature.obrigatorio,
      position: signature.ordem
    };
  }

  private getNameLabel(signature) {
    if (signature.rotulo) {
      return signature.rotulo;
    }
    if (signature.assinaturaMontada) {
      return signature.assinaturaMontada;
    }
    return null;
  }

  private initAutoCompleteSignature(): void {
    this.filteredSignatures = this.autoCompleteSignatureCtrl.valueChanges.pipe(
      startWith(''),
      map(option => option ? this._filter(option) : this.availableSignatures.slice())
    );
  }

  public disableDeleteButton(selectedSignature: Signatures): boolean {
    let isDisabled = false;
    if (!selectedSignature) {
      return isDisabled;
    }
    this.consentCheckboxOptionsData.map(consent => {
      const findSignature = consent.assinaturas.find(signature => signature.idTipo === selectedSignature.id);
      if (findSignature && findSignature.obrigatorio === '1') {
        isDisabled = true;
      }
    });
    return isDisabled;
  }

  public removeChipSignature(index: number): void {
    this.formControlSignatures.removeAt(index);
  }

  public selectedChipSignature(event: MatAutocompleteSelectedEvent): void {
    this.formControlSignatures.push(this.formBuilderSignature({
      idTipo: event.option.value.id,
      rotulo: event.option.value.assinatura,
      field_id_to_save_signature: this.getValueFieldIdToSaveLegitimateInterests()
    }));
    this.signatureInput.nativeElement.value = '';
    this.autoCompleteSignatureCtrl.setValue(null);
    this.updatePositionsOfTheSignaturesOnTheForm();
  }

  private getValueFieldIdToSaveLegitimateInterests() {
    const fieldIdToSave = this.form.get('field_id_to_save_legitimate_interests').value;
    if (fieldIdToSave) {
      return fieldIdToSave.field_id;
    }
    return fieldIdToSave;
  }

  private _filter(value) {
    if (typeof value === 'string') {
      const filterValue = value.toLowerCase();
      return this.availableSignatures.filter((option) =>
        option.assinatura.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').match(filterValue)).sort((a, b) => {
          if (a.assinatura.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') <
            b.assinatura.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')) {
            return -1;
          } else if (a.assinatura.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') >
            b.assinatura.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')) {
            return 1;
          } else {
            return 0;
          }
        });
    } else {
      return [];
    }
  }

  public asIs(): number { return 0; }
}
